/**
 * Shared hook that creates and memoizes a Graph API client instance.
 *
 * Site and list IDs are hardcoded (see config/sharepoint.ts), so the client
 * becomes ready as soon as the user is authenticated — no Graph resolution
 * round-trips. This is what lets a Limited-Access B2B guest in: the old
 * by-name site/list lookups required web-level read the guest lacks and died
 * with a 403 before the app could ever read AP_Master.
 */

import { useRef, useState, useEffect } from 'react';
import type { Client } from '@microsoft/microsoft-graph-client';
import { useAuth } from '../auth/useAuth';
import { createGraphClient } from '../services/graphClient';
import { createApiClient } from '../services/apiClient';
import { SHAREPOINT_SITE_ID, LIST_IDS } from '../config/sharepoint';
import type { ListNameKey } from '../config/sharepoint';

/** Resolved list IDs keyed by list name */
export type ListIds = Record<ListNameKey, string>;

export interface UseGraphClientResult {
  client: Client | null;
  siteId: string | null;
  listIds: ListIds | null;
  isReady: boolean;
  error: string | null;
}

export function useGraphClient(): UseGraphClientResult {
  const { isAuthenticated, isAP, getGraphToken, getApiToken } = useAuth();

  // Keep the latest token getters in refs so the client closure always calls
  // the current one without the client being re-created. Updated in an effect.
  const getTokenRef = useRef(getGraphToken);
  const getApiTokenRef = useRef(getApiToken);
  useEffect(() => {
    getTokenRef.current = getGraphToken;
    getApiTokenRef.current = getApiToken;
  }, [getGraphToken, getApiToken]);

  // Client lives in state (not a ref) so readiness is reactive — consumers
  // re-render when it appears.
  //
  // AP (B2B guest) users get an ApiClient that routes through the backend API
  // (SharePoint refuses guests' delegated Graph tokens). Internal staff get the
  // direct Graph client. The ApiClient is carried as `Client` (a cast) so the
  // feature services are identical for both — graphClient/documentLibraryService
  // detect it at runtime and branch.
  const [client, setClient] = useState<Client | null>(null);

  useEffect(() => {
    if (!isAuthenticated) {
      setClient(null);
      return;
    }
    setClient(
      isAP
        ? (createApiClient(() => getApiTokenRef.current()) as unknown as Client)
        : createGraphClient(() => getTokenRef.current()),
    );
  }, [isAuthenticated, isAP]);

  // IDs are static constants — ready the moment the client exists. No Graph
  // calls, so there is no async resolution that can fail or leave the AP shell
  // stuck on a loading/almost-there screen.
  const ready = isAuthenticated && client != null;

  return {
    client,
    siteId: ready ? SHAREPOINT_SITE_ID : null,
    listIds: ready ? LIST_IDS : null,
    isReady: ready,
    error: null,
  };
}
